import Order from "../../../classes/Order";
import { db } from "../../../boot/firebase";
import {
  collection,
  deleteField,
  doc,
  getDocs,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { timestampToDate } from "../../../util/misc";
import { isBoolean, sortBy, uniqBy } from "lodash";

const hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);

// Pre-existing sales had `status.vendors` keys with dots (e.g. "Acme.Inc"),
// which Firestore interprets as nested-field-paths on write. Wash those
// legacy keys out whenever we rewrite the map so the sale status doesn't
// keep a phantom entry on top of the canonical flat key.
const pruneLegacyVendorStatusKey = (vendors, vendorName) => {
  const segments = (vendorName || "").split(".");
  if (segments.length < 2) {
    return;
  }

  const [rootKey, ...childSegments] = segments;
  const rootStatus = vendors[rootKey];
  if (
    !rootStatus ||
    typeof rootStatus !== "object" ||
    hasOwn(rootStatus, "fullyOrdered")
  ) {
    return;
  }

  let cursor = rootStatus;
  for (const childSegment of childSegments) {
    if (!cursor || typeof cursor !== "object" || !(childSegment in cursor)) {
      return;
    }
    cursor = cursor[childSegment];
  }

  if (cursor && typeof cursor === "object" && hasOwn(cursor, "fullyOrdered")) {
    delete vendors[rootKey];
  }
};

const buildVendorStatusMap = (vendors, vendorName, fullyOrdered) => {
  const updatedVendors = { ...(vendors || {}) };
  const currentVendorStatus = updatedVendors[vendorName];

  updatedVendors[vendorName] = {
    ...(currentVendorStatus && typeof currentVendorStatus === "object"
      ? currentVendorStatus
      : {}),
    fullyOrdered,
  };

  pruneLegacyVendorStatusKey(updatedVendors, vendorName);
  return updatedVendors;
};

export const GET_ORDER = async (
  { dispatch },
  { projectID, saleID, orderID }
) => {
  return dispatch("GET_DOC", {
    converter: Order,
    path: Order.getPath(projectID, saleID, orderID),
    stateKey: "orders",
    id: orderID,
  });
};

export const GET_ORDERS = (
  { dispatch },
  { projectID, saleID, conditions, limitCount, pagination }
) => {
  const path = projectID && saleID ? Order.getPath(projectID, saleID) : null;
  return dispatch("GET_COLLECTION", {
    converter: Order,
    stateKey: "orders",
    colGroup: conditions && !path ? "invoices" : null,
    path,
    conditions,
    limitCount,
    pagination,
  });
};

export const LISTEN_ORDER = async (
  { dispatch },
  { projectID, saleID, orderID, next, error, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: Order,
    path: Order.getPath(projectID, saleID, orderID),
    stateKey: "orders",
    id: orderID,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_ORDERS = (
  { dispatch },
  { projectID, saleID, conditions, listenerPath, error, next, unlisten }
) => {
  const path = projectID && saleID ? Order.getPath(projectID, saleID) : null;
  return dispatch("LISTEN_COLLECTION", {
    converter: Order,
    stateKey: "orders",
    path,
    colGroup: conditions || !path ? "orders" : null,
    listenerPath,
    conditions,
    next,
    error,
    unlisten,
  });
};

export const SAVE_ORDER = async (
  { dispatch, state },
  { projectID, saleID, order, isFinalOrder }
) => {
  const batch = writeBatch(db);

  const saveDocuments = (existingData) => {
    return dispatch("SAVE_DOCUMENTS", {
      batch,
      parentData: order.data,
      fieldPath: "document",
      folderPath: order.path,
      isSingle: true,
      existingData,
    });
  };

  const updateSale = async (changes = {}) => {
    const saleChanges = { ...changes };

    if (isBoolean(isFinalOrder)) {
      const sale = await dispatch("GET_SALE", { projectID, saleID });
      saleChanges["status.vendors"] = buildVendorStatusMap(
        sale.data.status.vendors,
        order.vendor,
        isFinalOrder
      );
    }

    if (Object.keys(saleChanges).length) {
      return dispatch("UPDATE_SALE_STATUS", {
        batch,
        projectID,
        saleID,
        changes: saleChanges,
      });
    }
  };

  if (!order.id) {
    // New Order
    order.data.created = new Date();
    order.data.createdBy = state.user.id;

    const ref = doc(collection(db, Order.getPath(projectID, saleID)));
    order.id = ref.id;

    await saveDocuments();
    batch.set(ref.withConverter(Order), order);
    await updateSale({
      [`status.orders.${order.id}`]: {
        fullyAcked: order.isLabor,
        notes: order.data.notes,
      },
    });
    await batch.commit();
  } else {
    // Existing Order
    order.data.modified = new Date();
    order.data.modifiedBy = state.user.id;
    const current = await dispatch("GET_ORDER", {
      projectID,
      saleID,
      orderID: order.id,
    });

    await saveDocuments(current.data);
    batch.set(doc(db, order.path).withConverter(Order), order);
    await updateSale({
      [`status.orders.${order.id}.notes`]: order.data.notes,
    });
    await batch.commit();
  }
  return order;
};

export const UPDATE_ORDER = (
  { state },
  { projectID, saleID, orderID, changes }
) => {
  changes.modified = new Date();
  changes.modifiedBy = state.user.id;
  return updateDoc(doc(db, Order.getPath(projectID, saleID, orderID)), changes);
};

export const DELETE_ORDER = async ({ dispatch }, order) => {
  const batch = writeBatch(db);
  const { projectID, saleID, id: orderID } = order;
  batch.delete(doc(db, Order.getPath(projectID, saleID, orderID)));

  // Cascade-delete the order's child docs (acks, packing-slips) so they don't
  // linger as orphans, and clean their entries out of sale.status so the
  // sale status can advance correctly.
  const orderPath = Order.getPath(projectID, saleID, orderID);
  const [acksSnap, packingSlipsSnap] = await Promise.all([
    getDocs(collection(db, `${orderPath}/acks`)),
    getDocs(collection(db, `${orderPath}/packing-slips`)),
  ]);
  acksSnap.forEach((d) => batch.delete(d.ref));
  packingSlipsSnap.forEach((d) => batch.delete(d.ref));

  // The service issues the acks recorded this order on (SAVE_ACK), one
  // apiece. The ack saved last is the one whose number the issue carries.
  const issueLinks = uniqBy(
    sortBy(
      acksSnap.docs.map((d) => d.data()).filter((ack) => ack.serviceIssueID),
      (ack) => -(timestampToDate(ack.modified || ack.created) || 0)
    ),
    "serviceIssueID"
  );

  const sale = await dispatch("GET_SALE", { projectID, saleID });
  const status = (sale && sale.data && sale.data.status) || {};
  const changes = {
    [`status.orders.${orderID}`]: deleteField(),
  };
  // Clean any sale.status.acks/packingSlips/bills entries pointing at this order.
  // Iterate the live sale.status maps (rather than relying on sub-doc IDs) so
  // pre-existing orphan entries on the same orderID also get cleaned up.
  Object.entries(status.acks || {}).forEach(([ackID, entry]) => {
    if (entry && entry.orderID === orderID) {
      changes[`status.acks.${ackID}`] = deleteField();
    }
  });
  Object.entries(status.packingSlips || {}).forEach(([psID, entry]) => {
    if (entry && entry.orderID === orderID) {
      changes[`status.packingSlips.${psID}`] = deleteField();
    }
  });
  Object.entries(status.bills || {}).forEach(([billID, entry]) => {
    if (entry && entry.orderID === orderID) {
      changes[`status.bills.${billID}`] = deleteField();
    }
  });

  if (!order.data.issues.length) {
    changes["status.vendors"] = buildVendorStatusMap(
      sale.data.status.vendors,
      order.data.vendor,
      false
    );
  }
  await dispatch("UPDATE_SALE_STATUS", {
    batch,
    projectID,
    saleID,
    changes,
  });
  await batch.commit();

  // Each issue drops the order, as when its ack is deleted alone
  // (DELETE_ACK). After the commit, and on its own.
  for (const ack of issueLinks) {
    try {
      await dispatch("UNLINK_SERVICE_ISSUE_FROM_ORDER", {
        issueID: ack.serviceIssueID,
        orderID,
        orderNumber: ack.vendorOrderNumber || "",
      });
    } catch (error) {
      dispatch("NOTIFY_ERROR", error);
    }
  }
};
